import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function Services() {
  const { content } = useSite();
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const wa = content ? `https://wa.me/${content.whatsapp}` : "#";
  const faq = content?.faq || [];
  const featured = services.find((s) => s.featured);
  const others = services.filter((s) => !s.featured);

  return (
    <div data-testid="services-page" className="pt-32 pb-16 px-5 max-w-7xl mx-auto">
      <p className="text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-4">Services</p>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-16 max-w-3xl leading-tight">
        Un accompagnement pour chaque étape de votre chemin
      </h1>

      {featured && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="glass rounded-3xl p-8 sm:p-12 mb-10 relative overflow-hidden"
        >
          <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-amber-500/10 blur-3xl" />
          <span className="text-xs font-mono-accent uppercase tracking-widest text-amber-400/80">{featured.tag || "Parcours phare"}</span>
          <h2 className="font-serif text-3xl sm:text-4xl text-amber-100 mt-3 mb-4">{featured.title}</h2>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed mb-6">{featured.description}</p>
          <div className="flex flex-wrap gap-6 text-sm text-amber-200/80 font-mono-accent mb-8">
            {featured.duration && <span>Durée · {featured.duration}</span>}
            {featured.price && <span>Tarif · {featured.price}</span>}
            <span>Format · WhatsApp &amp; Zoom</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-amber-300 to-yellow-600 text-[#08080A] font-medium hover:scale-[1.03] transition-transform">
              Réserver une consultation <ArrowRight size={16} />
            </Link>
            <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition-colors">
              <MessageCircle size={16} /> WhatsApp
            </a>
          </div>
        </motion.div>
      )}

      <div className="grid gap-8 md:grid-cols-2 mb-24">
        {others.map((s, i) => (
          <motion.div
            key={s.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.1 }}
            className="glass rounded-2xl p-8 flex flex-col"
          >
            {s.tag && <span className="text-xs font-mono-accent uppercase tracking-widest text-amber-400/80 mb-3">{s.tag}</span>}
            <h3 className="font-serif text-2xl text-amber-100 mb-3">{s.title}</h3>
            <p className="text-slate-400 leading-relaxed flex-1 mb-6">{s.description}</p>
            <div className="flex flex-wrap gap-4 text-xs text-amber-200/70 font-mono-accent">
              {s.duration && <span className="inline-flex items-center gap-1"><Check size={12} /> {s.duration}</span>}
              {s.price && <span className="inline-flex items-center gap-1"><Check size={12} /> {s.price}</span>}
            </div>
          </motion.div>
        ))}
      </div>

      {faq.length > 0 && (
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-3xl sm:text-4xl text-amber-50 mb-8 text-center">Questions fréquentes</h2>
          <Accordion type="single" collapsible className="w-full">
            {faq.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-amber-500/15">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left text-amber-100 hover:text-amber-300 font-sans">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-400 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
    </div>
  );
}
